import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import HelicopterIcon from '@rsos/assets/icons/src/HelicopterIcon';
import { Box, Text } from '@rsos/base-components';
import { createWebMercatorViewport } from '../utils';
import {
  StyledPinnedAirAmbulancePopover,
  StyledAirAmbulancePopoverCaret,
  IconWrapper,
} from './MapPopover.styles';

const MemoizedAirAmbulancePopover = React.memo(({ x, y, location }) => {
  const coord = {
    x: x || 0,
    y: y || 0,
  };

  return (
    <StyledPinnedAirAmbulancePopover x={coord.x} y={coord.y} height={65}>
      <Box pt={2} m="0" justify="center" align="center">
        <Box horizontal px={0} pb={2} m={0} justify="center" align="center">
          <IconWrapper pr={1} justify="center" color={'background'}>
            <HelicopterIcon width="16px" height="16px" />
          </IconWrapper>
          <Text size="large" color="background" ellipsis>
            Air Ambulance
          </Text>
        </Box>
        <Text size="small" color="background" ellipsis>
          {`${parseFloat(location.latitude).toFixed(5)}, ${parseFloat(
            location.longitude,
          ).toFixed(5)}`}
        </Text>
        <StyledAirAmbulancePopoverCaret />
      </Box>
    </StyledPinnedAirAmbulancePopover>
  );
});

const AirAmbulancePopover = ({ overlay }) => {
  const [airAmbulancePosition, setPinnedPosition] = useState(null);
  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const airAmbulancePopover = useSelector(
    state => state.mapPopover.airAmbulancePopover,
  );

  /**
   * Repositions the popover based on the current viewState.
   * @param {Object} viewState
   */
  const reposition = useCallback(
    (viewState, airAmbulancePopover) => {
      const viewport = createWebMercatorViewport(viewState, basemap);

      if (airAmbulancePopover) {
        try {
          const [x, y] = viewport.project([
            airAmbulancePopover.longitude,
            airAmbulancePopover.latitude,
          ]);
          const viewportHeight = viewport.height;
          const viewportWidth = viewport.width;
          // hide airAmbulancePopover when it moves it out of view
          if (y > viewportHeight || x > viewportWidth || x < 0 || y < 0) {
            setPinnedPosition(null);
          } else {
            setPinnedPosition({ x, y });
          }
        } catch {
          /* suppress, fail gracefully */
        }
      }
    },
    [setPinnedPosition, basemap],
  );

  // Watch for viewState changes.
  useEffect(() => {
    if (overlay) {
      overlay.onViewStateChange(viewState => {
        reposition(viewState, airAmbulancePopover);
      });
    }
  }, [overlay, airAmbulancePopover, reposition]);

  // Reposition whenever the popover data changes.
  useEffect(() => {
    if (overlay && airAmbulancePopover) {
      const viewState = overlay.getViewState();
      reposition(viewState, airAmbulancePopover);
    }

    if (!airAmbulancePopover) {
      setPinnedPosition(null);
    }
  }, [overlay, airAmbulancePopover, reposition]);

  return (
    <>
      {airAmbulancePosition && airAmbulancePopover && (
        <MemoizedAirAmbulancePopover
          x={airAmbulancePosition?.x}
          y={airAmbulancePosition?.y}
          location={airAmbulancePopover}
        />
      )}
    </>
  );
};

export default AirAmbulancePopover;
